'use client'

import { ReactECharts } from '@/components/Charts/ReactECharts'
import { SeriesOption } from 'echarts'
import { getYear, parse } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import useDevice from '@/hooks/useDevice'
import * as Icons from '@/components/Icons/Klima'
import { ClimateIndex, ClimateIndices, IndicesTypes } from './dt'
import RequestIndicator from '@/components/Elements/RequestIndicator'
import { getThemeColor } from '@/utils/colors'
import { chartTooltipFormatter } from '@/utils/chart'
import AxisLabel from '@/components/Tiles/Base/AxisLabel'
import IndiciesToggle from '@/components/Tiles/Base/IndiciesToggle'
import Slider from '@/components/Inputs/Slider'
import {
  getTrendlineSeries,
  useLiveData,
} from '@schleegleixner/react-statamic-api'

const colorLookup: Record<
  IndicesTypes,
  'blue' | 'primary' | 'economy' | 'green' | 'society'
> = {
  eistage: 'blue',
  frosttage: 'primary',
  heisse_tage: 'society',
  sommertage: 'green',
  tropennaechte: 'economy',
}

const STARTING_YEAR = 1990

const YEAR_COMPARE_LABEL = 'Jahres-vgl.'

const MONTH_LABELS = [
  'JAN',
  'FEB',
  'MÄR',
  'APR',
  'MAI',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OKT',
  'NOV',
  'DEZ',
]

// static meta per index, decoupled from the actual series data
type IndexMeta = Pick<
  ClimateIndices[IndicesTypes],
  'title' | 'icon' | 'variant'
> & { name: string; default_visible: boolean }

const INDEX_META: Record<IndicesTypes, IndexMeta> = {
  heisse_tage: {
    title: 'heiße Tage (>= 30°C)',
    name: 'heiße Tage',
    icon: Icons.MsKlimadashboardIconsKlimakenntageHeiss,
    variant: 'society',
    default_visible: true,
  },
  sommertage: {
    title: 'Sommertage (>= 25°C)',
    name: 'Sommertage',
    icon: Icons.MsKlimadashboardIconsKlimakenntageSommer,
    variant: 'green',
    default_visible: false,
  },
  tropennaechte: {
    title: 'Tropennächte (>= 20°C)',
    name: 'Tropennächte',
    icon: Icons.MsKlimadashboardIconsKlimakenntageTropennacht,
    variant: 'economy',
    default_visible: false,
  },
  frosttage: {
    title: 'Frosttage (Min. < 0°C)',
    name: 'Frosttage',
    icon: Icons.MsKlimadashboardIconsKlimakenntageFrost,
    variant: 'primary',
    default_visible: false,
  },
  eistage: {
    title: 'Eistage (Max. < 0°C)',
    name: 'Eistage',
    icon: Icons.MsKlimadashboardIconsKlimakenntageEis,
    variant: 'blue',
    default_visible: false,
  },
}

const parseTimestamp = (timestamp: string) =>
  parse(timestamp, 'yyyy-MM-dd HH:mm:ssXXX', new Date())

// yearly view: sum monthly values per year (current, incomplete year excluded)
const getSeries = (data: ClimateIndex[], property: keyof ClimateIndex) => {
  const arr = data
    .filter(e => new Date(e.timestamp).getFullYear() >= STARTING_YEAR)
    .map(e => [parseTimestamp(e.timestamp), e[property]])
    .reduce((acc: Record<string, number>, [timestamp, value]) => {
      const year = getYear(timestamp as Date)

      // if current year, ignore
      if (year === new Date().getFullYear()) {
        return acc
      }

      acc[year] = (acc[year] ?? 0) + (value as number)

      return acc
    }, {})

  return Object.entries(arr).map(([year, value]) => [
    `${year}-01-01T00:00:00.000Z`,
    value,
  ])
}

// monthly view: 12 values (JAN..DEZ) for the selected year, missing months = null
const getMonthlySeries = (
  data: ClimateIndex[],
  property: keyof ClimateIndex,
  year: number,
): (number | null)[] => {
  const months: (number | null)[] = new Array(12).fill(null)

  data.forEach(e => {
    const date = parseTimestamp(e.timestamp)
    if (getYear(date) === year) {
      months[date.getMonth()] = e[property] as number
    }
  })

  return months
}

// last 10 years that actually have data
const getAvailableYears = (data: ClimateIndex[]): number[] => {
  const years = Array.from(
    new Set(data.map(e => getYear(parseTimestamp(e.timestamp)))),
  ).sort((a, b) => a - b)

  return years.slice(-10)
}

/**
 * Climate Indices Chart
 * @returns The Climate Indices Chart
 */
export default function ClimateIndicesChart() {
  const device = useDevice()

  const { data, status } = useLiveData<ClimateIndex[]>(
    'climate_indices',
    3600,
    false,
  )

  // null = year comparison (default), otherwise the selected year -> monthly view
  const [selected_year, setSelectedYear] = useState<number | null>(null)

  const [visibility, setVisibility] = useState<Record<IndicesTypes, boolean>>(
    () =>
      Object.fromEntries(
        (Object.keys(INDEX_META) as IndicesTypes[]).map(key => [
          key,
          INDEX_META[key].default_visible,
        ]),
      ) as Record<IndicesTypes, boolean>,
  )

  const years = useMemo(() => (data ? getAvailableYears(data) : []), [data])

  const indices = useMemo<ClimateIndices | null>(() => {
    if (!data) {
      return null
    }

    return (Object.keys(INDEX_META) as IndicesTypes[]).reduce((acc, key) => {
      const meta = INDEX_META[key]
      acc[key] = {
        title: meta.title,
        icon: meta.icon,
        variant: meta.variant,
        visible: visibility[key],
        seriesOption: {
          name: meta.name,
          color: getThemeColor(colorLookup[key]),
          data:
            selected_year === null
              ? getSeries(data, key)
              : getMonthlySeries(data, key, selected_year),
        },
      }
      return acc
    }, {} as ClimateIndices)
  }, [data, visibility, selected_year])

  // gatekeeper
  if (!indices || status !== 'success') {
    return (
      <div className="align-center flex h-full w-full justify-center">
        <RequestIndicator failed={status === 'error'} />
      </div>
    )
  }

  const toggleIndex = (key: string, visible: boolean) =>
    setVisibility(prev => ({ ...prev, [key as IndicesTypes]: visible }))

  const is_monthly = selected_year !== null

  const series: SeriesOption[] = Object.keys(indices)
    .filter(key => indices[key as IndicesTypes]?.visible)
    .map(e => ({
      ...indices[e as IndicesTypes].seriesOption,
      type: 'line',
      itemStyle: {
        opacity: 1,
        borderColor: '#fff',
        borderWidth: 2,
      },
      symbol: 'circle',
      showAllSymbol: true,
      symbolSize: 7,
      areaStyle: {
        color: getThemeColor(colorLookup[e as IndicesTypes] ?? 'primary'),
        opacity: 0.05,
      },
    }))

  const trendlineSeries = getTrendlineSeries(series, {
    lineStyle: {
      type: 'dotted',
      color: getThemeColor('primary'),
      opacity: 0.3,
    },
  })

  const activeToggleCount = series.length

  // slider: last 10 years + "Jahres-vgl." (default preselected)
  const slider_labels = [...years.map(String), YEAR_COMPARE_LABEL]

  return (
    <div className="relative flex h-full w-full flex-col items-center py-4 2xl:flex-row">
      <div className="flex h-full w-full flex-1 flex-col">
        <AxisLabel>Anzahl der Tage</AxisLabel>
        <div className="h-[235px] w-full md:h-[440px]">
          <ReactECharts
            option={{
              grid: {
                top: 20,
                bottom: 40,
                left: 40,
                right: 40,
              },
              tooltip: {
                trigger: 'axis',
                formatter: params =>
                  is_monthly
                    ? chartTooltipFormatter(
                        (params as any[]).map(p => ({
                          ...p,
                          axisValue: `${p.axisValue} ${selected_year}`,
                        })),
                        indices,
                      )
                    : chartTooltipFormatter(params, indices),
              },
              series: [
                ...series,
                ...(!is_monthly && activeToggleCount === 1
                  ? [trendlineSeries]
                  : []),
              ],
              xAxis: is_monthly
                ? {
                    type: 'category',
                    data: MONTH_LABELS,
                    axisLabel: {
                      fontSize: device === 'mobile' ? 12 : 20,
                      interval: 0,
                    },
                    splitLine: {
                      show: true,
                    },
                  }
                : {
                    type: 'time',
                    axisLabel: {
                      fontSize: device === 'mobile' ? 12 : 20,
                    },
                    min: parse(
                      `${STARTING_YEAR}-01-01`,
                      'yyyy-MM-dd',
                      new Date(),
                    ).getTime(),
                    max: parse(
                      `${new Date().getFullYear()}-01-01`,
                      'yyyy-MM-dd',
                      new Date(),
                    ).getTime(),
                    splitLine: {
                      show: true,
                    },
                  },
              yAxis: {
                type: 'value',
                axisLabel: {
                  fontSize: device === 'mobile' ? 12 : 20,
                  formatter: (val: any) => {
                    if (val === 0) {
                      return ''
                    }
                    return val
                  },
                },
              },
              animation: true,
            }}
            settings={{
              notMerge: true,
            }}
          />
        </div>

        <Slider
          className="mt-4 w-full"
          default_value={slider_labels.length - 1}
          labels={slider_labels}
          max={slider_labels.length - 1}
          min={0}
          onValueChange={index =>
            setSelectedYear(index === years.length ? null : years[index])
          }
          variant="primary"
        />
      </div>

      <IndiciesToggle indices={indices} onToggle={toggleIndex} />
    </div>
  )
}
